import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { API_CONFIG, getNodeApiHeaders } from '../../helpers/constants';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  cloud_id?: string;
  site_url?: string;
}

const JiraOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setError(`OAuth error: ${error}`);
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        // Construct redirectUri based on current origin
        const redirectUri = `${window.location.origin}/auth/jira/callback`;

        // Call the server endpoint to exchange code for tokens
        const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/jira/generateToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getNodeApiHeaders(),
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        setTokenData(tokenResponse);
        setLoading(false);

        // Send data back to parent window and close
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              app_id: state,
              provider: 'jira',
              ...tokenResponse,
              cloud_id: tokenResponse.cloud_id || '',
              site_url: tokenResponse.site_url || ''
            }
          }, window.location.origin);
          window.close();
        }
      } catch (err) {
        console.error('Error processing OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams]);

  const handleGoBack = () => {
    navigate('/supportSpace');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Authenticating to Jira...
        </Typography>
      </Box>
    );
  }

  // If we have a parent window, don't show the success screen - just close
  if (window.opener && !error) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoBack}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              ✅ Jira Authentication Successful!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your Jira workspace has been successfully authenticated.
            </Typography>

            <Button variant="contained" onClick={handleGoBack}>
              Continue
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default JiraOAuthCallback;

